
import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ShoppingBag,
    LocalShipping,
    TrendingUp,
    Sync,
    Print,
    QrCode,
    PriceCheck,
    ArrowForward,
    CloudDownload,
    Refresh,
    Description
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { b2cOpsAPI } from '../../api';

interface StatsByStatus {
    processing: number;
    pending: number;
    completed: number;
    on_hold: number;
    cancelled: number;
    refunded: number;
    failed: number;
    total: number;

    // Revenue fields
    processing_revenue: number;
    pending_revenue: number;
    completed_revenue: number;
    on_hold_revenue: number;
    cancelled_revenue: number;
    refunded_revenue: number;
    failed_revenue: number;
    total_revenue: number;
}


interface DashboardStats {
    today: StatsByStatus;
    yesterday: StatsByStatus;
    week_to_date: StatsByStatus;
    month_to_date: StatsByStatus;
    last_updated: string;
}

export default function B2CManagementDashboard() {
    const navigate = useNavigate();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await b2cOpsAPI.getDashboardStats();
            setStats(data);
        } catch (err) {
            console.error("Failed to fetch dashboard stats:", err);
            setError("Failed to load dashboard statistics. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const quickActions = [
        {
            title: "Order Extractor V2",
            desc: "Unified export for Excel, Labels & PDFs",
            icon: <CloudDownload fontSize="large" color="primary" />,
            path: "/b2c-ops/order_extractor_v2"
        },
        {
            title: "Label Generator",
            desc: "Generate Shipping Labels manually",
            icon: <Print fontSize="large" color="secondary" />,
            path: "/b2c-ops/label-generator"
        },
        {
            title: "MRP Labels",
            desc: "Generate MRP Stickers for inventory",
            icon: <QrCode fontSize="large" color="action" />,
            path: "/b2c-ops/mrp-label-generator"
        },
        {
            title: "Delivery Split Generator",
            desc: "Generate Delivery Run Sheets",
            icon: <Description fontSize="large" color="primary" />,
            path: "/b2c-ops/delivery-split-generator"
        },
        {
            title: "Price Monitor",
            desc: "Check competitor pricing & stock",
            icon: <PriceCheck fontSize="large" color="error" />,
            path: "/b2c-ops/price-monitor"
        }
    ];

    // Helper to format number
    const f = (n: number | undefined) => n || 0;

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Typography variant="h4" gutterBottom component="div" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                        B2C Operations Center
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Overview of daily operations and quick access to tools
                    </Typography>
                </div>
                <Box>
                    <Button
                        startIcon={<Refresh />}
                        onClick={fetchStats}
                        sx={{ mr: 2 }}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <Button variant="contained" startIcon={<Sync />} onClick={() => alert('Triggering Smart Sync...')}>
                        Sync All Data
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {/* Top Cards - Using Real Data where possible */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                                    <ShoppingBag />
                                </Box>
                                <Chip label="Today" size="small" variant="outlined" />
                            </Box>
                            <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                                {loading ? <CircularProgress size={20} /> : stats?.today.total}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Orders Today
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                                    <LocalShipping />
                                </Box>
                                <Chip label="Pending" size="small" color="warning" variant="outlined" />
                            </Box>
                            <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                                {loading ? <CircularProgress size={20} /> : stats?.today.processing}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Processing (Today)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                                    <TrendingUp />
                                </Box>
                                <Chip label="Trend" size="small" color="success" variant="outlined" />
                            </Box>
                            <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                                {loading ? <CircularProgress size={20} /> : `₹${f(stats?.today.total_revenue).toLocaleString()}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Daily Revenue
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                                    <Sync />
                                </Box>
                                <Chip label="System" size="small" color="info" variant="outlined" />
                            </Box>
                            <Typography variant="h6" sx={{ mt: 2, mb: 0.5, fontWeight: 'bold' }}>
                                V2 Active
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Unified Extractor Ready
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Detailed Stats Table */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'medium' }}>
                Order Statistics
            </Typography>
            <Paper elevation={2} sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: 'grey.100' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Today</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Yesterday</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>This Week<br /><Typography variant="caption" color="text.secondary">(Sun - Today)</Typography></TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Month to Date<br /><Typography variant="caption" color="text.secondary">(1st - Today)</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {[
                                        { key: 'processing', label: 'Processing' },
                                        { key: 'pending', label: 'Pending Payment' },
                                        { key: 'completed', label: 'Completed' },
                                        { key: 'on_hold', label: 'On Hold' },
                                        { key: 'cancelled', label: 'Cancelled' },
                                        { key: 'refunded', label: 'Refunded' },
                                    ].map((row) => (
                                        <TableRow key={row.key} hover>
                                            <TableCell component="th" scope="row">
                                                {row.label}
                                            </TableCell>
                                            <TableCell align="right">{f(stats?.today[row.key as keyof StatsByStatus])}</TableCell>
                                            <TableCell align="right">{f(stats?.yesterday[row.key as keyof StatsByStatus])}</TableCell>
                                            <TableCell align="right">{f(stats?.week_to_date[row.key as keyof StatsByStatus])}</TableCell>
                                            <TableCell align="right">{f(stats?.month_to_date[row.key as keyof StatsByStatus])}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{f(stats?.today.total)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{f(stats?.yesterday.total)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{f(stats?.week_to_date.total)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{f(stats?.month_to_date.total)}</TableCell>
                                    </TableRow>
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Revenue Stats Table */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'medium' }}>
                Revenue Statistics
            </Typography>
            <Paper elevation={2} sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: 'grey.100' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Today</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Yesterday</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>This Week</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Month to Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {[
                                        { key: 'processing', label: 'Processing' },
                                        { key: 'completed', label: 'Completed' },
                                        { key: 'total', label: 'Total Revenue' },
                                    ].map((row) => {
                                        const revKey = row.key === 'total' ? 'total_revenue' : `${row.key}_revenue`;
                                        return (
                                            <TableRow key={row.key} hover sx={row.key === 'total' ? { bgcolor: 'grey.50', '& td': { fontWeight: 'bold' } } : {}}>
                                                <TableCell component="th" scope="row">
                                                    {row.label}
                                                </TableCell>
                                                <TableCell align="right">₹{f(stats?.today[revKey as keyof StatsByStatus]).toLocaleString()}</TableCell>
                                                <TableCell align="right">₹{f(stats?.yesterday[revKey as keyof StatsByStatus]).toLocaleString()}</TableCell>
                                                <TableCell align="right">₹{f(stats?.week_to_date[revKey as keyof StatsByStatus]).toLocaleString()}</TableCell>
                                                <TableCell align="right">₹{f(stats?.month_to_date[revKey as keyof StatsByStatus]).toLocaleString()}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'medium' }}>
                Tools & Modules
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                {quickActions.map((action, index) => (
                    <Grid item xs={12} md={6} lg={3} key={index}>
                        <Card
                            elevation={3}
                            sx={{
                                height: '100%',
                                cursor: 'pointer',
                                transition: '0.3s',
                                '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 }
                            }}
                            onClick={() => navigate(action.path)}
                        >
                            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                <Box sx={{ mb: 2 }}>
                                    {action.icon}
                                </Box>
                                <Typography variant="h6" gutterBottom>
                                    {action.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {action.desc}
                                </Typography>
                                <Button endIcon={<ArrowForward />} size="small">
                                    Open
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
